import { FC, useState, useMemo } from "react"
import { useQueryClient } from "react-query"
import { Formik, Form, FormikValues, getIn } from "formik"
import * as Yup from 'yup'
import {
  FormikFileUpload,
  StatusErrorMessage,
  FormikNotesArea
} from "../../../../../components/widgets"

import { doFormSubmit, processNoteValues } from "../../../../../components/helpers"

import { pickBy} from "lodash"
import { DLMROI, submitDLMPhase_3_1_4_1 } from "../../../../core/doclabel"
import { GenerateInspectionROIModal } from "../components/GenerateInspectionROIModal"
import { SubmitButtonRow } from "../../../../../components/layout"

const DLM_ROI_SELECTED_FIELDS = [
  'roiId',
  'issueDate',
]

const initInspectionApprovalValues = {
  roiModels: [],
  note: '',
  noteAttachments: [],
}

const schema = Yup.object({
  roiModels: Yup.array().of(
    Yup.object().shape({
      roiId: Yup.number().integer().label('ROI Id'),
      roiAttachmentId: Yup.number().integer().moreThan(0,'ROI required').required().label('ROI Attachment'),
      roiAttachmentIdAttachmentName: Yup.string().trim(),
    })
  ).required(),
  note: Yup.string().trim().label('Note'),
  noteAttachments: Yup.array(),
})

type Props = {
  id: number
  appID: number
  rois: Array<DLMROI>
  doClose: Function
}

const InspectionApprovalForm: FC<Props> = ({id, appID, rois, doClose}) => {
  const queryClient = useQueryClient()
  

  const [roiModel, setROIModel] = useState<any>(null);
  const [roiIndex, setROIIndex] = useState(-1);
  const [show_genroi, showGenROI] = useState(false);

  const initValues = useMemo(() => {
    let _inits = {
      ...initInspectionApprovalValues
    };

    if (! (rois && rois.length > 0)) return _inits;
    
    Object.assign(_inits, { 
      roiModels: Array.from({ length: rois.length }, () => ({ 
        roiId: 0,
        roiAttachmentId: 0,
        roiAttachmentIdAttachmentName: ''
      }))
    });
      
    return _inits;

  }, [rois])

  const closeROIModal = () => {
    showGenROI(false);
  }

  const doSubmit = async(values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;
    const _models = values.roiModels.map( (model: any) => pickBy(model, (value, key) => (value && (! key.match(/AttachmentName$/i)))));

    const payload = {
      ...pickBy(values, (value, key) => (value && (! key.match(/(note|AttachmentName|roiModels)$/i)))),
      siteApplicationid: id,
      roiModels: _models
    };

    processNoteValues(values, payload);

    await doFormSubmit(submitDLMPhase_3_1_4_1, payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'label-applications-list'}]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      doClose();
    });
  }

  return(
  <>
  <Formik
    initialValues={initValues}
    validationSchema={schema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
  {({status, setFieldValue, values, validateField}) => {

    const saveROIValues = (values: any) => {
      if (! (values && typeof(values) === 'object')) return;
      setFieldValue(`roiModels.${roiIndex}`, {
        ...pickBy(values, (value, key) => (value && (DLM_ROI_SELECTED_FIELDS.indexOf(key) > -1))),
        roiAttachmentId : 0,
        roiAttachmentIdAttachmentName: ''
      }, true);
    }

    return (
    <>
    <Form id='kt_doclabel_inspection_approval_form' className='form' noValidate>
      <div
        className='d-flex flex-column p-2'
      >
        <div className="fw-bold fs-6 mb-2">Upload ROI:</div>
        <div className='text-muted fs-7'>For COC Holder Endorsement</div>
        <div className='separator border-gray-200 mb-2'></div>
        <div className="mh-300px overflow-auto pe-4">        
          {rois.map((roiModel: any, idx: number) => (
          <div key={`setROIrow_${idx}`} className="row">
            <div className="col-3">
              <button
                type="button"
                className='btn btn-sm btn-light-secondary py-1 my-2 px-3'
                onClick={() => {
                    setROIModel(roiModel);
                    setROIIndex(idx);
                    showGenROI(true);
                }}
              >
                <span className='indicator-label'>Generate ROI</span>
              </button>
            </div>
            <FormikFileUpload
              size="9"
              label={`[${roiModel?.cbRef ? roiModel.cbRef : `${roiModel.series}${roiModel.no}${roiModel?.split ? roiModel?.split :''}`}] ${roiModel.projectName}`}
              name={`roiModels.${idx}.roiAttachmentId`}
              acceptFiles="UserFileFormats"
              required={true}
              disabled={getIn(values, `roiModels.${idx}.roiId`) ? false : true}
              subTitle={getIn(values, `roiModels.${idx}.roiId`) ? "Upload ROI here" : "Generate ROI first."}
              onUpdateFunc={ () => {
                validateField('roiModels');
              }}
            />
          </div>
          ))
          }
        </div>
        <FormikNotesArea />
        <StatusErrorMessage status={status} />
        <SubmitButtonRow doClose={doClose} />
      </div>
    </Form>
    { (show_genroi && roiModel) &&
    <GenerateInspectionROIModal hasIssueDate={true} baseROI={roiModel} onROIGenerated={saveROIValues} doClose={closeROIModal} />
    }
    </>
    )
    }}
  </Formik>
  </>
  )
}

export { InspectionApprovalForm }